import React, { useState, useEffect } from 'react';
import { Calendar, Sparkles } from 'lucide-react';

interface ContributionDay {
  date: string;
  contributionCount: number;
  color: string;
}

interface GitHubContributions {
  weeks: Array<{
    contributionDays: ContributionDay[];
  }>;
}

const GitHubContributions = () => {
  const [contributions, setContributions] = useState<number[][]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [hoveredDay, setHoveredDay] = useState<{ count: number; date: string } | null>(null);

  useEffect(() => {
    const fetchGitHubData = async () => {
      try {
        setIsLoading(true);
        const mockData = generateMockData();
        setContributions(processContributionData(mockData));
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to fetch GitHub data');
      } finally {
        setIsLoading(false);
      }
    };

    fetchGitHubData();
  }, []);

  const generateMockData = (): GitHubContributions => {
    return {
      weeks: Array(52).fill(null).map(() => ({
        contributionDays: Array(7).fill(null).map(() => ({
          date: new Date().toISOString(),
          contributionCount: Math.floor(Math.random() * 5),
          color: '#40c463'
        }))
      }))
    };
  };

  const processContributionData = (data: GitHubContributions) => {
    return data.weeks.map(week => 
      week.contributionDays.map(day => day.contributionCount)
    );
  };

  const getContributionColor = (level: number) => {
    const colors = {
      0: 'bg-slate-100 dark:bg-slate-800/50',
      1: 'bg-slate-300 dark:bg-slate-700/50',
      2: 'bg-slate-400 dark:bg-slate-600/70',
      3: 'bg-slate-500 dark:bg-slate-500/80',
      4: 'bg-slate-600 dark:bg-slate-400'
    };
    return colors[level as keyof typeof colors] || colors[0];
  };

  const getTooltipText = (level: number) => {
    if (level === 0) return 'No contributions';
    if (level === 1) return '1-3 contributions';
    if (level === 2) return '4-6 contributions';
    if (level === 3) return '7-9 contributions';
    return '10+ contributions';
  };

  if (isLoading) {
    return (
      <div className="bg-slate-50/80 dark:bg-slate-900/80 rounded-2xl p-6 shadow-lg backdrop-blur-sm">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-slate-200 dark:bg-slate-800 rounded w-1/3"></div>
          <div className="h-32 bg-slate-200 dark:bg-slate-800 rounded"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-slate-50/80 dark:bg-slate-900/80 rounded-2xl p-6 shadow-lg backdrop-blur-sm">
        <div className="flex items-center gap-2 text-red-500">
          <span>{error}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-slate-50/80 dark:bg-slate-900/80 rounded-2xl p-6 shadow-lg backdrop-blur-sm animate-fade-in">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-2 animate-fade-in" style={{ animationDelay: '200ms' }}>
          <div className="p-2 bg-gradient-to-r from-slate-600 to-slate-500 dark:from-slate-500 dark:to-slate-400 rounded-lg relative group">
            <Calendar className="w-5 h-5 text-white" />
            <div className="absolute -top-1 -right-1 opacity-0 group-hover:opacity-100 transition-all duration-300">
              <Sparkles className="w-4 h-4 text-slate-300 animate-pulse" />
            </div>
          </div>
          <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
            Contribution Graph
          </h3>
        </div>
        <div className="text-sm text-slate-600 dark:text-slate-400 animate-fade-in" style={{ animationDelay: '300ms' }}>
          Past year
        </div>
      </div>
      
      {/* Contribution Grid with Enhanced Animations */}
      <div className="overflow-x-auto">
        <div className="flex gap-1 min-w-max">
          {contributions.map((week, weekIndex) => (
            <div key={weekIndex} className="flex flex-col gap-1">
              {week.map((day, dayIndex) => (
                <div
                  key={`${weekIndex}-${dayIndex}`}
                  className={`
                    w-3 h-3 rounded-sm ${getContributionColor(day)}
                    hover:scale-125 hover:ring-2 hover:ring-slate-400 dark:hover:ring-slate-500
                    transition-all duration-300 cursor-pointer
                    animate-fade-in
                  `}
                  style={{ 
                    animationDelay: `${(weekIndex * 7 + dayIndex) * 20}ms`,
                    transformOrigin: 'center'
                  }}
                  onMouseEnter={() => setHoveredDay({ count: day, date: new Date().toISOString() })}
                  onMouseLeave={() => setHoveredDay(null)}
                  title={getTooltipText(day)}
                />
              ))}
            </div>
          ))}
        </div>
      </div>

      {/* Enhanced Tooltip */}
      {hoveredDay && (
        <div 
          className="absolute transform -translate-y-full mt-2 px-3 py-2 bg-slate-800 text-slate-100 text-sm rounded-lg shadow-lg animate-fade-in"
          style={{ animationDuration: '150ms' }}
        >
          <div className="font-medium">{hoveredDay.count} contributions</div>
          <div className="text-xs text-slate-400">
            {new Date(hoveredDay.date).toLocaleDateString('en-US', {
              weekday: 'long',
              year: 'numeric',
              month: 'long',
              day: 'numeric'
            })}
          </div>
        </div>
      )}
      
      {/* Legend with Animation */}
      <div className="flex items-center justify-between mt-4 text-xs text-slate-600 dark:text-slate-400 animate-fade-in" style={{ animationDelay: '1000ms' }}>
        <span className="animate-fade-in" style={{ animationDelay: '1200ms' }}>Less</span>
        <div className="flex gap-1">
          {[0, 1, 2, 3, 4].map((level, index) => (
            <div
              key={level}
              className={`
                w-3 h-3 rounded-sm ${getContributionColor(level)}
                hover:scale-110 transition-transform
                animate-fade-in
              `}
              style={{ animationDelay: `${1000 + index * 100}ms` }}
            />
          ))}
        </div>
        <span className="animate-fade-in" style={{ animationDelay: '1600ms' }}>More</span>
      </div>
    </div>
  );
};

export default GitHubContributions;
